/**
 * Business Cards — admin.
 *
 * Issue a digital card, download its QR for the printer, and work the contacts
 * it captured. Reachable at Marketing › Business Cards.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Contact, Copy, Check, Download, ExternalLink, Loader2, Plus, QrCode, Users,
} from 'lucide-react';

import { apiRequest, apiDownload } from '../../services/api';
import { Button } from '../../components/new-components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/new-components/ui/dialog';

type BusinessCard = {
  id: string;
  slug: string;
  full_name: string;
  job_title?: string | null;
  organization?: string | null;
  email?: string | null;
  phone?: string | null;
  linkedin_url?: string | null;
  website_url?: string | null;
  headline?: string | null;
  is_active: boolean;
  scan_count: number;
  contact_count: number;
  public_url: string;
  qr_url: string;
};

type CapturedContact = {
  id: string;
  name: string;
  company?: string | null;
  email: string;
  phone?: string | null;
  consent_contact: boolean;
  created_at: string;
};

type ApiResult<T> = { success: boolean; data: T; error?: string };

/**
 * apiDownload returns a Blob rather than saving it, so the anchor dance is the
 * caller's job. Same pattern as EventFunnels.
 */
async function saveAs(url: string, filename: string) {
  const blob = await apiDownload(url);
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(href);
}

const EMPTY = {
  full_name: '', job_title: '', organization: 'eMerge Senior Care',
  email: '', phone: '', linkedin_url: '', website_url: '', headline: '',
};

export default function BusinessCards() {
  const [cards, setCards] = useState<BusinessCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  const [qrFor, setQrFor] = useState<BusinessCard | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const [contactsFor, setContactsFor] = useState<BusinessCard | null>(null);
  const [contacts, setContacts] = useState<CapturedContact[]>([]);

  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await apiRequest('GET', '/admin/business-cards')) as ApiResult<BusinessCard[]>;
      setCards(res.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load business cards');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // Blank optional fields are omitted rather than sent as "", which the
      // API's url() validators would reject.
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => String(v).trim() !== '')
      );
      const res = (await apiRequest('POST', '/admin/business-cards', payload)) as ApiResult<BusinessCard>;
      if (!res.success) throw new Error(res.error || 'Could not create the card');
      setCreating(false);
      setForm({ ...EMPTY });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the card');
    } finally {
      setSaving(false);
    }
  }, [form, load]);

  const openQr = useCallback(async (card: BusinessCard) => {
    setQrFor(card);
    setQrDataUrl(null);
    const res = (await apiRequest('GET', `/admin/business-cards/${card.id}/qr-preview`)) as
      ApiResult<{ data_url: string; encodes: string }>;
    setQrDataUrl(res.data.data_url);
  }, []);

  const openContacts = useCallback(async (card: BusinessCard) => {
    setContactsFor(card);
    setContacts([]);
    const res = (await apiRequest('GET', `/admin/business-cards/${card.id}/contacts`)) as
      ApiResult<CapturedContact[]>;
    setContacts(res.data || []);
  }, []);

  const copy = useCallback(async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
    } catch { /* clipboard blocked; the link is visible and selectable anyway */ }
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Business Cards</h1>
          <p className="text-sm text-muted-foreground mt-1">
            A QR code for a printed card. Scanning it opens a contact page and captures a lead.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4 mr-2" /> New card
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading
        </div>
      ) : cards.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Contact className="w-8 h-8 mx-auto mb-3 opacity-50" />
            <p>No business cards yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {cards.map((card) => (
            <Card key={card.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">{card.full_name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {[card.job_title, card.organization].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {!card.is_active && <Badge variant="secondary">Inactive</Badge>}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm">
                  <code className="flex-1 truncate rounded bg-muted px-2 py-1 text-xs">
                    {card.public_url}
                  </code>
                  <Button variant="ghost" size="sm" onClick={() => copy(card.public_url, card.id)}>
                    {copied === card.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <a href={card.public_url} target="_blank" rel="noreferrer noopener">
                    <Button variant="ghost" size="sm"><ExternalLink className="w-4 h-4" /></Button>
                  </a>
                </div>

                <div className="flex gap-6 text-sm">
                  {/* tabular-nums so the two counters line up across cards */}
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <strong>{card.scan_count}</strong>
                    <span className="text-muted-foreground"> scans</span>
                  </span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <strong>{card.contact_count}</strong>
                    <span className="text-muted-foreground"> contacts</span>
                  </span>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => openQr(card)}>
                    <QrCode className="w-4 h-4 mr-2" /> QR
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openContacts(card)}>
                    <Users className="w-4 h-4 mr-2" /> Contacts
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => saveAs(
                      `/admin/business-cards/${card.id}/contacts.csv`,
                      `${card.slug}-contacts.csv`
                    )}
                  >
                    <Download className="w-4 h-4 mr-2" /> CSV
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── New card ─────────────────────────────────────────────────── */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New business card</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {([
              ['full_name', 'Full name', 'Harold Kirksey'],
              ['job_title', 'Title', 'CEO'],
              ['organization', 'Organization', 'eMerge Senior Care'],
              ['email', 'Email', 'HKirksey@eMergeSC.com'],
              ['phone', 'Phone', '813-200-7111'],
              ['website_url', 'Website', 'https://www.eMergeSC.com'],
              ['linkedin_url', 'LinkedIn', 'https://www.linkedin.com/in/…'],
              ['headline', 'Headline (optional)', 'One short line under the name'],
            ] as const).map(([key, label, placeholder]) => (
              <div key={key}>
                <label className="text-sm font-medium" htmlFor={`bc-${key}`}>{label}</label>
                <Input
                  id={`bc-${key}`}
                  value={(form as Record<string, string>)[key]}
                  placeholder={placeholder}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              </div>
            ))}
            <p className="text-xs text-muted-foreground">
              The link is derived from the name and cannot be changed later — it gets printed.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} disabled={saving || !form.full_name.trim()}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── QR ───────────────────────────────────────────────────────── */}
      <Dialog open={Boolean(qrFor)} onOpenChange={(open) => !open && setQrFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{qrFor?.full_name} — QR code</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            {qrDataUrl
              ? <img src={qrDataUrl} alt="QR code" className="w-56 h-56" />
              : <Loader2 className="w-6 h-6 animate-spin" />}
            <code className="text-xs text-muted-foreground break-all text-center">
              {qrFor?.qr_url}
            </code>
            <p className="text-xs text-muted-foreground text-center">
              Error correction H — it still scans with about a third of the code obscured,
              which is what survives print and handling. Send the printer the SVG.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => qrFor && saveAs(
                  `/admin/business-cards/${qrFor.id}/qr.svg`, `${qrFor.slug}-qr.svg`
                )}
              >
                <Download className="w-4 h-4 mr-2" /> SVG (print)
              </Button>
              <Button
                variant="outline"
                onClick={() => qrFor && saveAs(
                  `/admin/business-cards/${qrFor.id}/qr.png`, `${qrFor.slug}-qr.png`
                )}
              >
                <Download className="w-4 h-4 mr-2" /> PNG
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Captured contacts ────────────────────────────────────────── */}
      <Dialog open={Boolean(contactsFor)} onOpenChange={(open) => !open && setContactsFor(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{contactsFor?.full_name} — captured contacts</DialogTitle>
          </DialogHeader>
          {contacts.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              No contacts captured yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Company</th>
                    <th className="py-2 pr-3 font-medium">Email</th>
                    <th className="py-2 pr-3 font-medium">Phone</th>
                    <th className="py-2 font-medium">Captured</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-b last:border-0">
                      <td className="py-2 pr-3">{c.name}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{c.company || '—'}</td>
                      <td className="py-2 pr-3">{c.email}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{c.phone || '—'}</td>
                      <td className="py-2 text-muted-foreground whitespace-nowrap">
                        {new Date(c.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
